import isNil from "lodash/isNil";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useShortlistFillAndBook } from "PFCore/hooks/queries/shortlists/use_shortlist_actions";
import { useShortlistCreate } from "PFCore/hooks/queries/shortlists/use_shortlist_create";
import { useErrorsGrowl } from "PFCore/hooks/use_errors_growl";
import { ShortlistBookingDates } from "PFCore/services/shortlist";
import { Activity, Id, ShortlistMinimized } from "PFTypes";
import { useCallback } from "react";

export type HandleFillAndBookParams = {
  activity: Pick<Activity, "id" | "parent_activity_id">;
  profileId: Id;
  shortlistId?: Id;
  vacancyId?: Id;
  named?: boolean;
};
type HandleFillAndBookActionParams = Omit<HandleFillAndBookParams, "named" | "shortlistId"> & {
  shortlistId: Id;
  bookings?: ShortlistBookingDates[];
};

type UseFillAndBookReturn = {
  isLoading: boolean;
  handleFillAndBook: (
    params: HandleFillAndBookParams,
    skipBookingsCreation?: boolean
  ) => Promise<ShortlistMinimized | undefined>;
  handleFillAndBookAction: (
    params: HandleFillAndBookActionParams,
    skipBookingsCreation?: boolean,
    onError?: (response) => void
  ) => Promise<ShortlistMinimized | undefined>;
};

export const useFillAndBook = (): UseFillAndBookReturn => {
  const growlError = useErrorsGrowl();

  const { mutateAsync: fillAndBook, isPending: isFillAndBooking } = useShortlistFillAndBook();
  const { mutateAsync: createShortlist, isPending: isCreatingShortlist } = useShortlistCreate();
  const { invalidateVacancies } = useActivityInvalidate();
  const { invalidateList } = useBookingInvalidate();

  const handleFillAndBookAction = useCallback<UseFillAndBookReturn["handleFillAndBookAction"]>(
    async (
      { activity, profileId, shortlistId, vacancyId, bookings },
      skipBookingsCreation = false,
      onError
    ) => {
      try {
        const response = await fillAndBook({
          shortlistId,
          skipBookingsCreation,
          activity,
          profileId: Number(profileId),
          ...(!isNil(vacancyId) ? { vacancyId: Number(vacancyId) } : {}),
          ...(bookings ? { bookings } : {})
        });

        await invalidateList();

        return response;
      } catch ({ response }) {
        growlError(response, { unique: true });
        onError?.(response);
      }
    },
    [fillAndBook, growlError, invalidateVacancies]
  );

  const handleFillAndBook = useCallback<UseFillAndBookReturn["handleFillAndBook"]>(
    async (
      { activity, profileId, shortlistId, vacancyId, named = true }: HandleFillAndBookParams,
      skipBookingsCreation: boolean = false
    ) => {
      try {
        const finalShortlistId =
          shortlistId || (await createShortlist({ activityId: activity.id, profileId, named })).id;

        return handleFillAndBookAction(
          { activity, profileId, shortlistId: finalShortlistId, vacancyId },
          skipBookingsCreation
        );
      } catch ({ response }) {
        growlError(response, { unique: true });
      }
    },
    [createShortlist, growlError, handleFillAndBookAction]
  );

  const isLoading = isFillAndBooking || isCreatingShortlist;

  return {
    isLoading,
    handleFillAndBook,
    handleFillAndBookAction
  };
};
